'use client';

import '@/styles/ag-grid.css'; // Mandatory CSS required by the Data Grid

import type { ColDef, Column, GridApi, IRowNode, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';
import clsx from 'clsx';
import { type FC,useEffect, useMemo, useState } from 'react';

import { suppressHeaderKeyboardEvent, suppressKeyboardEvent } from '@/utils/gridUtils';

import {
  CreditsToolTip,
  GradesToolTip,
} from '../academicRecords/history/fullCourseHistory/TooltipData';
import TooltipComp from '../common/Tooltip/Tooltip';
import { CardHeader, CardTitle } from './card';

export interface CustomDataTableProps extends AgGridReactProps {
  gridHeight?: string;
  gridWidth?: string;
  breakpoint?: number;
  mobileView?: boolean;
  tabIndex?: number;
  disableTableCellFocus?: boolean;
  isSortGridRefresh?: boolean;
}
const DataTable: FC<CustomDataTableProps> = ({
  rowData,
  columnDefs,
  gridHeight = 'auto',
  gridWidth = '100%',
  defaultColDef = {},
  breakpoint = 768,
  tabIndex = 0,
  disableTableCellFocus = false,
  isSortGridRefresh = false,
  ...props
}) => {
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= breakpoint);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const updateScreenSize = () => {
    setIsLargeScreen(window.innerWidth >= breakpoint);
  };

  useEffect(() => {
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const ColDefSuppressKeyboardEvents = useMemo<ColDef>(() => {
    return {
      suppressHeaderKeyboardEvent: (event) =>
        suppressHeaderKeyboardEvent({ ...event, disableHeaderCellFocus: disableTableCellFocus }),
      suppressKeyboardEvent,
    };
  }, []);

  const onSortChanged = () => {
    if (gridApi) {
      gridApi.redrawRows();
    }
  };

  return (
    <div>
      {isLargeScreen ? (
        <div style={{ height: gridHeight, width: gridWidth }}>
          <AgGridReact
            rowData={rowData}
            onGridReady={(params) => setGridApi(params.api)}
            onSortChanged={() => (isSortGridRefresh ? onSortChanged() : null)}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            rowSelection="multiple"
            suppressRowClickSelection
            tooltipShowDelay={0}
            tabIndex={tabIndex}
            tooltipInteraction
            containerStyle={{ overflow: 'none' }}
            defaultColDef={{
              resizable: false,
              suppressMovable: true,
              sortable: false,
              sortingOrder: ['asc', 'desc'],
              wrapText: true,
              ...ColDefSuppressKeyboardEvents,
              ...defaultColDef,
            }}
            {...props}
          />
        </div>
      ) : (
        <div>
          {rowData?.map((item, rowIndex) => (
            <div key={`${item.termSemester}-${item.CLASS_NBR ?? rowIndex}`} className="my-2">
              {columnDefs?.map((colItem: ColDef, colIndex) => {
                const key = `${item.termSemester}-${colItem.field}-${colIndex}`;
                const params: ValueGetterParams = {
                  data: item,
                  colDef: colItem,
                  column: { getColDef: () => colItem } as Column,
                  node: {} as IRowNode,
                  getValue: (field: string) => item[field],
                  api: {} as GridApi,
                  context: {} as unknown,
                };
                return (
                  <div key={key}>
                    {colIndex === 0 ? (
                      <CardHeader className="bg-spgray p-2">
                        <CardTitle element="h3" className="font-sans font-normal text-spblack">
                          {typeof colItem.valueGetter === 'function'
                            ? colItem?.valueGetter(params)
                            : item[colItem?.field ?? '']}
                        </CardTitle>
                      </CardHeader>
                    ) : (
                      <div className="flex flex-col border-b border-gray-200 px-4 py-2">
                        <div className="flex items-center gap-3 font-sans font-semibold text-spblack">
                          {colItem?.headerName}{' '}
                          {colItem?.headerName === 'Grade' && item.id === 0 && (
                            <TooltipComp
                              contentDescription={<GradesToolTip />}
                              linkDescription=""
                              id="Grade"
                              btnAria="Grade Info"
                              dataTestIdName="info"
                            />
                          )}
                          {colItem?.headerName === 'Credits' && item.id === 0 && (
                            <TooltipComp
                              contentDescription={<CreditsToolTip />}
                              linkDescription=""
                              id="Credits"
                              btnAria="Credits"
                              dataTestIdName="info"
                            />
                          )}
                        </div>
                        <div className={clsx('pl-4 font-sans text-spblack', colItem?.cellClass)}>
                          {typeof colItem.valueGetter === 'function'
                            ? colItem?.valueGetter(params)
                            : item[colItem?.field ?? '']}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DataTable;
